import { useEffect, useRef, useState } from 'react';
import { WbCard, WbGrid, WbGridCol, WbGridRow, WbHeading } from '@workbench/react';
import { useTranslation } from 'react-i18next';

import {
	_getAvailableBackgrounds,
	_getImage,
	_getVehicleImages,
} from '../../../Utils/Vimdt/apiCalls';
import _notification from '../../../Utils/Vimdt/notification';
import _handleErrors from '../../../Utils/Vimdt/handleErrors';
import { convertImage } from '../../../Utils/Vimdt/convertImage';
import {
	addToCachedData,
	getFromCache,
	hashString,
	STORAGE_KEYS,
} from '../../../Utils/Vimdt/storage';
import { useImageContext } from '../../../Contexts/ImageContext';

import VimdtButton from '../Common/VimdtButton';
import VimdtInput from '../Common/VimdtInput';
import VimdtSwitch from '../Common/VimdtSwitch';
import VimdtSelect from '../Common/VimdtSelect';
import VimdtAccordion from '../Common/VimdtAccordion';
import PanelHeader from '../Common/PanelHeader';
import VimdtProductKey from '../Common/VimdtProductKey';
import ImageList from './ImageList';
import ExampleImageList from './ExampleImageList';
import { loadExampleImages } from './exampleImages';
import './Images.scss';

const CACHE_TTL_MS = 7200000;
const BLUR_CROP_POSITIONS = ['both-blur', 'top-blur', 'bottom-blur'];

const fileFormatOptions = (background) => {
	const options = [
		{ id: 'png', name: 'PNG', value: 'png' },
		{ id: 'webp', name: 'WEBP', value: 'webp' },
	];
	if (background) options.push({ id: 'jpeg', name: 'JPEG', value: 'jpeg' });
	return options;
};

const generateFileNameByVin = (vins) => {
	if (vins.length > 1) return `BULK_VINS_${vins.length}`;
	return vins[0];
};

const vinCheck = (vins) => ({
	isValid: vins.length <= 5 && vins.length !== 0,
	isBulk: vins.length > 1,
});

const cleanAndSplitVINs = (vin) =>
	vin
		.replace(/ +/g, '')
		.split(',')
		.filter(Boolean);

const revokeImageListUrls = (list) => {
	Object.values(list || {}).forEach((images) =>
		(images || []).forEach(({ url }) => url && (window.URL || window.webkitURL).revokeObjectURL(url))
	);
};

function Images({ rowCount = 6 }) {
	const { t, i18n } = useTranslation();
	const {
		apikey,
		setApikey,
		imageList,
		setImageList,
		background,
		setBackground,
	} = useImageContext();

	const [vin, setVin] = useState(null);
	const [night, setNight] = useState(false);
	const [roofOpen, setRoofOpen] = useState(false);
	const [cropped, setCropped] = useState(false);
	const [uniformImage, setUniformImage] = useState('standard');
	const [isImagesLoading, setIsImagesLoading] = useState(false);
	const [fileFormat, setFileFormat] = useState('png');
	const [cropPosition, setCropPosition] = useState('both');
	const [noFallback, setNoFallback] = useState(false);
	const [examples, setExamples] = useState(false);
	const [bulk, setBulk] = useState(false);
	const [saveFileName, setSaveFileName] = useState('');
	const [settingsPanels, setSettingsPanels] = useState([]);

	// Snapshot of the settings used for the most recent download.
	const [loadedFileFormat, setLoadedFileFormat] = useState('');
	const [loadedBackground, setLoadedBackground] = useState(false);
	const [loadedCropped, setLoadedCropped] = useState(false);

	const [backgroundSelection, setBackgroundSelection] = useState(false);
	const [availableBackgrounds, setAvailableBackgrounds] = useState([]);
	const [selectedBackground, setSelectedBackground] = useState('');
	const [defaultBackground, setDefaultBackground] = useState('');
	const [lastBackgroundVin, setLastBackgroundVin] = useState('');

	const notifyKey = useRef();
	const prevLoadedVins = useRef({});

	useEffect(() => {
		prevLoadedVins.current = imageList;
	}, [imageList]);

	useEffect(
		() => () => revokeImageListUrls(prevLoadedVins.current),
		[]
	);

	const openNotification = (title, message, key) => {
		notifyKey.current = _notification({
			title,
			message,
			key: key || notifyKey.current,
			onClose: () => {
				notifyKey.current = undefined;
			},
		});
	};

	const imageSettingsDataTable = [
		{ key: 1, type: t('vimdt.components.light'), desc: t('vimdt.imagesTab.infoTable.lightDesc') },
		{ key: 2, type: t('vimdt.components.roof'), desc: t('vimdt.imagesTab.infoTable.roofDesc') },
		{
			key: 3,
			type: t('vimdt.components.background'),
			desc: t('vimdt.imagesTab.infoTable.backgroundDesc'),
		},
		{
			key: 4,
			type: t('vimdt.components.backgroundScene'),
			desc: t('vimdt.imagesTab.infoTable.backgroundSelectDesc'),
		},
		{
			key: 5,
			type: t('vimdt.components.fileFormat'),
			desc: t('vimdt.imagesTab.infoTable.fileFormatDesc'),
		},
		{
			key: 6,
			type: t('vimdt.components.imageFormat'),
			desc: t('vimdt.imagesTab.infoTable.imageFormatDesc'),
		},
		{
			key: 7,
			type: t('vimdt.components.cropPosition'),
			desc: t('vimdt.imagesTab.infoTable.cropPositionDesc'),
		},
		{
			key: 8,
			type: t('vimdt.components.vehiclePosition'),
			desc: t('vimdt.imagesTab.infoTable.vehiclePositionDesc'),
		},
		{
			key: 9,
			type: t('vimdt.components.fallback'),
			desc: t('vimdt.imagesTab.infoTable.fallbackDesc'),
		},
	];

	const cropPositionOptions = [
		{ id: 'both', name: t('vimdt.imagesTab.crop.both'), value: 'both' },
		{ id: 'top', name: t('vimdt.imagesTab.crop.top'), value: 'top' },
		{ id: 'bottom', name: t('vimdt.imagesTab.crop.bottom'), value: 'bottom' },
		...(background
			? [
					{ id: 'both-blur', name: t('vimdt.imagesTab.crop.bothBlur'), value: 'both-blur' },
					{ id: 'top-blur', name: t('vimdt.imagesTab.crop.topBlur'), value: 'top-blur' },
					{ id: 'bottom-blur', name: t('vimdt.imagesTab.crop.bottomBlur'), value: 'bottom-blur' },
			  ]
			: []),
	];

	const positionOptions = [
		{ id: 'standard', value: 'standard', name: t('vimdt.imagesTab.position.standard') },
		{ id: 'stage', value: 'stage', name: t('vimdt.imagesTab.position.centerStage') },
		{ id: 'center', value: 'center', name: t('vimdt.imagesTab.position.center') },
		{ id: 'wheel', value: 'wheel', name: t('vimdt.imagesTab.position.frontWheel') },
		{ id: 'middle', value: 'middle', name: t('vimdt.imagesTab.position.middle') },
	];

	const vinValidationNotification = (vins) => {
		if (vins.length > 5) {
			openNotification(t('vimdt.notification.warning'), t('vimdt.notification.invalidVINCount'));
		} else if (vins.length === 1) {
			openNotification(t('vimdt.notification.warning'), t('vimdt.notification.invalidVINDesc'));
		}
	};

	const shouldIncludeBackgroundItemQueryParam = (vinsLength) =>
		background &&
		backgroundSelection &&
		vinsLength === 1 &&
		defaultBackground !== selectedBackground &&
		(selectedBackground.length === 3 || selectedBackground.length === 2) &&
		selectedBackground.startsWith('T') &&
		lastBackgroundVin === vin;

	const getImage = (imageId) =>
		_getImage({ apikey, imageId }).then((resp) => resp.data);

	const loadImages = async (newImageList, vinElement) => {
		const candidates = newImageList.filter((image) => image.imageId !== '');

		const loadedImages = await Promise.all(
			candidates.map(async ({ imageId, title }) => {
				try {
					const imageData = await getImage(imageId);
					return { imageId, title, ...convertImage(imageData, fileFormat) };
				} catch (err) {
					_notification({
						title: t('vimdt.notification.error'),
						message: `${t('vimdt.notification.failedImage')}${vinElement} - ${title}`,
						error: true,
					});
					console.error(err);
					return { imageId, title, url: '', blob: null };
				}
			})
		);

		loadedImages.sort(
			(a, b) =>
				candidates.findIndex((e) => e.imageId === a.imageId) -
				candidates.findIndex((e) => e.imageId === b.imageId)
		);

		setImageList((prev) => ({ ...prev, [vinElement]: loadedImages.filter(Boolean) }));
	};

	const getVehicleImagesByVIN = async (vinElement, params) => {
		if (vinElement.length !== 17) {
			openNotification(
				t('vimdt.notification.warning'),
				t('vimdt.notification.invalidVINLongDesc')
			);
			return;
		}

		const requestParams = { ...params, vin: vinElement };

		if (!background && uniformImage !== 'standard') {
			requestParams.queries.uniformImage = uniformImage;
		}

		setIsImagesLoading(true);
		setImageList((prev) => ({ ...prev, [vinElement]: [] }));
		openNotification(t('vimdt.notification.loading'), `Getting images for: ${vinElement}`, vinElement);

		const cachedFIN = getFromCache(requestParams);
		const vehicleImages = cachedFIN
			? Promise.resolve(cachedFIN.imageList)
			: _getVehicleImages(requestParams).then((resp) => {
					if (!resp.data) return [];
					const list = Object.keys(resp.data).map((key) => ({
						title: key,
						imageId: resp.data[key],
					}));
					addToCachedData(STORAGE_KEYS.IMAGES, {
						vin: vinElement,
						apikeyHash: hashString(apikey),
						imageList: list,
						expiry: new Date().getTime() + CACHE_TTL_MS,
						night,
						fallback: !noFallback,
						roofOpen,
						background,
						uniformImage: requestParams.queries.uniformImage,
						fileFormat,
						cropPosition,
						cropped,
						backgroundItem: requestParams.queries.backgroundItem,
					});
					return list;
			  });

		try {
			const newImageList = await vehicleImages;
			await loadImages(newImageList, vinElement);
			setIsImagesLoading(false);
			openNotification(
				t('vimdt.notification.finished'),
				t('vimdt.notification.loadedImages') + vinElement,
				vinElement
			);
		} catch (err) {
			const errorMessage = _handleErrors(err, vinElement);
			setImageList((prev) => ({
				...prev,
				[vinElement]: [...(prev?.[vinElement] || []), { errorMessage }],
			}));
			setIsImagesLoading(false);
			openNotification(t('vimdt.notification.error'), errorMessage);
		}
	};

	const getVehicleImages = async () => {
		setExamples(false);

		if (!apikey || !vin) return;

		const vins = cleanAndSplitVINs(vin);
		const { isValid, isBulk } = vinCheck(vins);

		if (!isValid) {
			vinValidationNotification(vins);
			return;
		}

		// A background item only applies to the VIN its list was loaded for.
		if (vin !== lastBackgroundVin && selectedBackground.startsWith('T')) {
			setAvailableBackgrounds([]);
			setSelectedBackground(t('vimdt.imagesTab.default'));
			setDefaultBackground('');
			setLastBackgroundVin('');
			openNotification(
				t('vimdt.notification.warning'),
				t('vimdt.notification.invalidBackgroundErrorMessage')
			);
			return;
		}

		setSaveFileName(generateFileNameByVin(vins));
		setBulk(isBulk);
		revokeImageListUrls(prevLoadedVins.current);
		setImageList({});
		prevLoadedVins.current = {};

		for (const vinElement of vins) {
			if (vinElement in prevLoadedVins.current) continue;

			setLoadedBackground(background);
			setLoadedFileFormat(fileFormat);
			setLoadedCropped(cropped);

			const params = {
				apikey,
				queries: {
					night,
					roofOpen,
					background,
					fileFormat,
					cropped,
					cropPosition,
					fallback: !noFallback,
				},
			};

			if (shouldIncludeBackgroundItemQueryParam(vins.length)) {
				params.queries.backgroundItem = selectedBackground;
			}

			// eslint-disable-next-line no-await-in-loop
			await getVehicleImagesByVIN(vinElement, params);
		}
	};

	const getExampleImages = async () => {
		revokeImageListUrls(prevLoadedVins.current);
		setImageList(await loadExampleImages());
		setExamples(true);
	};

	const loadBackgrounds = async () => {
		if (!apikey || !vin || vin.includes(',')) return;

		try {
			const response = await _getAvailableBackgrounds({ vin, apikey });

			if (response.status === 200) {
				const backgrounds = response.data.backgrounds;
				const defaultBg = Object.keys(backgrounds.default)[0];
				setDefaultBackground(defaultBg);

				const describe = (bg, fallbackText) =>
					i18n.exists(`vimdt.imagesTab.backgrounds.${bg}`)
						? t(`vimdt.imagesTab.backgrounds.${bg}`)
						: fallbackText;

				const backgroundList = [
					{
						id: defaultBg,
						name: `${describe(defaultBg, backgrounds.default.description)} (${t(
							'vimdt.imagesTab.default'
						)})`,
						value: defaultBg,
					},
				];

				Object.keys(backgrounds.available)
					.filter((bg) => bg !== defaultBg && bg !== 'T9' && bg !== 'T99')
					.forEach((bg) => {
						backgroundList.push({
							id: bg,
							name: describe(bg, backgrounds.available[bg].description),
							value: bg,
						});
					});

				setSelectedBackground(defaultBg);
				setAvailableBackgrounds(backgroundList);
				setLastBackgroundVin(vin);
			} else {
				setSelectedBackground(t('vimdt.imagesTab.default'));
				setAvailableBackgrounds([]);
			}

			setBackgroundSelection(true);
		} catch (err) {
			setSelectedBackground(t('vimdt.imagesTab.default'));
			setAvailableBackgrounds([]);
			setBackgroundSelection(true);
			openNotification(t('vimdt.notification.error'), _handleErrors(err, vin));
		}
	};

	const backgroundOnChange = (enabled) => {
		setBackground(enabled);
		setAvailableBackgrounds([]);
		setSelectedBackground(t('vimdt.imagesTab.default'));
		setDefaultBackground('');
		setLastBackgroundVin('');

		if (fileFormat === 'jpeg') setFileFormat('png');
		if (!enabled && BLUR_CROP_POSITIONS.includes(cropPosition)) setCropPosition('both');

		openNotification(
			t('vimdt.components.background'),
			`${t('vimdt.notification.switched')} ${
				enabled ? t('vimdt.notification.backgroundWith2') : t('vimdt.notification.backgroundWithout2')
			}`,
			vin
		);
	};

	const settings = (
		<WbGrid fullWidth>
			<WbGridRow className="vimdt-images__settings">
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSwitch
						label={t('vimdt.components.light')}
						onBtn={t('vimdt.components.dayLight')}
						offBtn={t('vimdt.components.nightLight')}
						status={[t('vimdt.components.dayLight'), t('vimdt.components.nightLight')]}
						getValue={setNight}
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSwitch
						label={t('vimdt.components.roof')}
						onBtn={t('vimdt.components.roofClosed')}
						offBtn={t('vimdt.components.roofOpen')}
						status={[t('vimdt.components.roofClosed'), t('vimdt.components.roofOpen')]}
						getValue={setRoofOpen}
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSwitch
						label={t('vimdt.components.background')}
						onBtn={t('vimdt.components.withoutBackground')}
						offBtn={t('vimdt.components.withBackground')}
						status={[
							t('vimdt.notification.backgroundWithout1'),
							t('vimdt.notification.backgroundWith1'),
						]}
						defaultValue={background}
						getValue={backgroundOnChange}
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSelect
						label={t('vimdt.components.backgroundScene')}
						value={selectedBackground}
						setValue={setSelectedBackground}
						options={availableBackgrounds}
						disabled={!background || !backgroundSelection}
						backgroundSelectMessage
						adjustableWith
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSelect
						label={t('vimdt.components.fileFormat')}
						value={fileFormat}
						setValue={setFileFormat}
						options={fileFormatOptions(background)}
						adjustableWith
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSwitch
						label={t('vimdt.components.imageFormat')}
						onBtn="16:9"
						offBtn="4:3"
						status={[
							t('vimdt.notification.imageFormat16_9'),
							t('vimdt.notification.imageFormat4_3'),
						]}
						getValue={setCropped}
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSelect
						label={`4:3 ${t('vimdt.components.cropPosition43')}`}
						value={cropPosition}
						setValue={setCropPosition}
						options={cropPositionOptions}
						disabled={!cropped}
						adjustableWith
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSelect
						label={t('vimdt.components.vehiclePosition')}
						value={uniformImage}
						setValue={setUniformImage}
						options={positionOptions}
						disabled={background}
						defaultValue="standard"
						adjustableWith
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={6} mq5={4}>
					<VimdtSwitch
						label={t('vimdt.components.fallback')}
						onBtn={t('vimdt.components.fallbackOn')}
						offBtn={t('vimdt.components.fallbackOff')}
						status={[t('vimdt.components.fallbackOn'), t('vimdt.components.fallbackOff')]}
						getValue={setNoFallback}
						defaultValue={noFallback}
					/>
				</WbGridCol>
			</WbGridRow>
		</WbGrid>
	);

	return (
		<div className="vimdt-images">
			<WbCard className="shared-tab-content-padding">

				<WbHeading size="m">{t('vimdt.imagesTab.downloadText')}</WbHeading>

				<WbGrid fullWidth>
					<WbGridRow className="vimdt-images__inputs">
						<WbGridCol mq1={12} mq3={6}>
							<VimdtInput
								label={t('vimdt.placeholders.apiKeyPlaceholder')}
								val={apikey}
								getValue={setApikey}
								password
								testId="vimdt-images-apikey"
							/>
						</WbGridCol>
						<WbGridCol mq1={12} mq3={6}>
							<VimdtInput
								label={t('vimdt.placeholders.vinNumberPlaceholder')}
								getValue={setVin}
								message={t('vimdt.imagesTab.vin')}
								testId="vimdt-images-vin"
							/>
						</WbGridCol>
					</WbGridRow>
				</WbGrid>

				<VimdtAccordion
					activeKeys={settingsPanels}
					onChange={setSettingsPanels}
					items={[
						{
							key: 'settings',
							header: (
								<PanelHeader
									headerName={t('vimdt.imagesTab.imagesSettings')}
									description={t('vimdt.imagesTab.imagesSettingsDesc')}
									data={imageSettingsDataTable}
								/>
							),
							content: settings,
						},
					]}
				/>

				<div className="vimdt-images__actions">
					<VimdtButton
						label={t('vimdt.loadSaveButtons.loadBackgrounds')}
						onClick={loadBackgrounds}
						disabled={!background}
					/>
					<VimdtButton
						label={t('vimdt.loadSaveButtons.loadSampleImages')}
						onClick={getExampleImages}
						disabled={isImagesLoading}
					/>
					<VimdtButton
						label={t('vimdt.loadSaveButtons.loadImages')}
						onClick={getVehicleImages}
						loading={isImagesLoading}
						disabled={!apikey || !vin}
					/>
				</div>
			</WbCard>

			<VimdtProductKey apikey={apikey} />

			{!examples && Object.keys(imageList).length > 0 && (
				<ImageList
					bulk={bulk}
					imageList={imageList}
					saveFileName={saveFileName}
					isImagesLoading={isImagesLoading}
					imageType={loadedFileFormat}
					rowCount={rowCount}
					background={background}
					cropped={loadedCropped}
					loadedBackground={loadedBackground}
					loadedFileFormat={loadedFileFormat}
				/>
			)}
			{examples && <ExampleImageList imageList={imageList} rowCount={rowCount} />}
		</div>
	);
}

export default Images;
